/**
 * Slide-out Cart JavaScript
 *
 * This script handles the functionality of the slide-out cart including:
 * - Opening and closing the cart
 * - Updating item quantities
 * - Removing items
 * - AJAX cart updates
 */

class SlideOutCart {
    constructor() {
      this.cart = document.getElementById("slide-out-cart");
      this.overlay = document.getElementById("slide-out-cart-overlay");
      this.cartForm = document.getElementById("cart-form");
      this.cartItems = this.cart?.querySelector(".slide-out-cart__items");
      this.isUpdating = false;

      this.init();
    }

    init() {
      // Initialize cart toggle buttons
      this.initCartToggle();

      // Initialize quantity buttons
      this.initQuantityButtons();

      // Initialize remove item buttons
      this.initRemoveButtons();

      // Initialize cart form submission
      this.initCartForm();

      // Listen for cart changes from other parts of the site
      document.addEventListener("cart:updated", this.updateCart.bind(this));
      this.fetchAndUpdateCart();
    }

    initCartToggle() {
      // Add click event to cart toggle buttons (usually in the header)
      const cartToggleButtons = document.querySelectorAll("[data-cart-toggle]");
      cartToggleButtons.forEach((button) => {
        button.addEventListener("click", this.toggleCart.bind(this));
      });

      // Close cart when clicking the close button
      const closeButton = this.cart?.querySelector(".slide-out-cart__close");
      if (closeButton) {
        closeButton.addEventListener("click", this.closeCart.bind(this));
      }

      // Close cart when clicking the overlay
      if (this.overlay) {
        this.overlay.addEventListener("click", this.closeCart.bind(this));
      }

      // Close cart when pressing ESC key
      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
          this.closeCart();
        }
      });
    }

    initQuantityButtons() {
      // Quantity increase buttons
      const plusButtons = this.cart?.querySelectorAll("[data-quantity-plus]");
      plusButtons?.forEach((button) => {
        button.addEventListener("click", this.increaseQuantity.bind(this));
      });

      // Quantity decrease buttons
      const minusButtons = this.cart?.querySelectorAll("[data-quantity-minus]");
      minusButtons?.forEach((button) => {
        button.addEventListener("click", this.decreaseQuantity.bind(this));
      });

      // Quantity input changes
      const quantityInputs = this.cart?.querySelectorAll("[data-quantity-input]");
      quantityInputs?.forEach((input) => {
        input.addEventListener("change", this.quantityChanged.bind(this));
      });
    }

    initRemoveButtons() {
      const removeButtons = this.cart?.querySelectorAll("[data-remove-item]");
      removeButtons?.forEach((button) => {
        button.addEventListener("click", this.removeItem.bind(this));
      });
    }

    initCartForm() {
      if (this.cartForm) {
        this.cartForm.addEventListener("submit", this.handleFormSubmit.bind(this));
      }
    }

    toggleCart(event) {
      if (event) {
        event.preventDefault();
      }

      if (this.cart?.classList.contains("is-active")) {
        this.closeCart();
      } else {
        this.openCart();
        // Fetch and update cart products when opening the cart
        this.fetchAndUpdateCart();
      }
    }

    openCart() {
      this.cart?.classList.add("is-active");
      this.overlay?.classList.add("is-active");
      document.body.classList.add("slide-out-cart-open");

      // Add loading state while fetching cart data
      this.cart?.classList.add("is-loading");
    }

    closeCart() {
      this.cart?.classList.remove("is-active");
      this.overlay?.classList.remove("is-active");
      document.body.classList.remove("slide-out-cart-open");
    }

    increaseQuantity(event) {
      const button = event.currentTarget;
      const wrapper = button.closest(".slide-out-cart__item-quantity");
      const input = wrapper?.querySelector("[data-quantity-input]");

      if (input) {
        const currentValue = parseInt(input.value, 10);
        input.value = currentValue + 1;
        this.updateItemQuantity(input);
      }
    }

    decreaseQuantity(event) {
      const button = event.currentTarget;
      const wrapper = button.closest(".slide-out-cart__item-quantity");
      const input = wrapper?.querySelector("[data-quantity-input]");

      if (input) {
        const currentValue = parseInt(input.value, 10);
        if (currentValue > 0) {
          input.value = currentValue - 1;
          this.updateItemQuantity(input);
        }
      }
    }

    quantityChanged(event) {
      const input = event.currentTarget;
      this.updateItemQuantity(input);
    }

    updateItemQuantity(input) {
      if (this.isUpdating) return;

      const newQuantity = parseInt(input.value, 10);
      const itemKey = input.closest("[data-cart-item]")?.dataset.lineItemKey;

      if (itemKey) {
        this.isUpdating = true;
        this.cart?.classList.add("is-loading");

        const formData = new FormData();
        formData.append("updates[" + itemKey + "]", newQuantity);
        formData.append("sections", "slide-out-cart");

        fetch(window.Shopify.routes.root + "cart/update.js", {
          method: "POST",
          body: formData,
        })
          .then((response) => response.json())
          .then((data) => {
            this.updateCartFromResponse(data);
          })
          .catch((error) => {
            console.error("Error updating cart:", error);
          })
          .finally(() => {
            this.isUpdating = false;
            this.cart?.classList.remove("is-loading");
          });
      }
    }

    removeItem(event) {
      const button = event.currentTarget;
      const item = button.closest("[data-cart-item]");
      const itemKey = item?.dataset.lineItemKey;

      if (itemKey) {
        this.isUpdating = true;
        this.cart?.classList.add("is-loading");

        const formData = new FormData();
        formData.append("updates[" + itemKey + "]", 0);
        formData.append("sections", "slide-out-cart");

        fetch(window.Shopify.routes.root + "cart/update.js", {
          method: "POST",
          body: formData,
        })
          .then((response) => response.json())
          .then((data) => {
            this.updateCartFromResponse(data);
          })
          .catch((error) => {
            console.error("Error removing item:", error);
          })
          .finally(() => {
            this.isUpdating = false;
            this.cart?.classList.remove("is-loading");
          });
      }
    }

    handleFormSubmit(event) {
      // Only handle form submission if it's not a checkout
      if (!event.submitter || event.submitter.name !== "checkout") {
        event.preventDefault();

        this.isUpdating = true;
        this.cart?.classList.add("is-loading");

        const formData = new FormData(this.cartForm);
        formData.append("sections", "slide-out-cart");

        fetch(window.Shopify.routes.root + "cart/update.js", {
          method: "POST",
          body: formData,
        })
          .then((response) => response.json())
          .then((data) => {
            this.updateCartFromResponse(data);
          })
          .catch((error) => {
            console.error("Error updating cart:", error);
          })
          .finally(() => {
            this.isUpdating = false;
            this.cart?.classList.remove("is-loading");
          });
      }
    }

    updateCartFromResponse(data) {
      // Update cart count in the header
      this.updateCartCount(data.item_count);

      // If the cart is empty, refresh the page to show empty cart state
      if (data.item_count === 0) {
        window.location.reload();
        return;
      }

      // Check if sections data is available
      if (data.sections && data.sections["slide-out-cart"]) {
        // Process sections data if available
        const parser = new DOMParser();
        const newCartHTML = parser.parseFromString(data.sections["slide-out-cart"], "text/html");
        const newCartItems = newCartHTML.querySelector(".slide-out-cart__items");
        const newCartFooter = newCartHTML.querySelector(".slide-out-cart__footer");

        if (newCartItems && this.cartItems) {
          this.cartItems.innerHTML = newCartItems.innerHTML;
        }

        const cartFooter = this.cart?.querySelector(".slide-out-cart__footer");
        if (newCartFooter && cartFooter) {
          cartFooter.innerHTML = newCartFooter.innerHTML;
        }

        // Reinitialize event listeners for the new elements
        this.initQuantityButtons();
        this.initRemoveButtons();
      } else {
        // If sections data is not available, fetch the cart data and update manually
        this.fetchAndUpdateCart();
      }
    }

    fetchAndUpdateCart() {
      // Set loading state
      this.isUpdating = true;

      fetch(window.Shopify.routes.root + "cart.js")
        .then((response) => response.json())
        .then((cartData) => {
          // Check if all items contain "special" in their title
          this.checkAndRemovespecialItems(cartData).then(updatedCartData => {
            // Update cart items based on the cart data
            this.updateCartItems(updatedCartData || cartData);

            // After updating cart items, update the upsell products
            this.updateUpsellProducts();
          });
        })
        .catch((error) => {
          console.error("Error fetching cart data:", error);
          // Remove loading state even if there's an error
          this.cart?.classList.remove("is-loading");
        })
        .finally(() => {
          // Reset updating flag
          this.isUpdating = false;
        });
    }

    checkAndRemovespecialItems(cartData) {
      // If no items, just return the original data
      if (!cartData.items || cartData.items.length === 0) {
        return Promise.resolve(cartData);
      }

      // Check if all items contain "special" in their title
      const hasNonspecialItems = cartData.items.some(item =>
        !item.product_title.toLowerCase().includes("special")
      );

      // If there are non-special items, no need to remove anything
      if (hasNonspecialItems) {
        console.log("Cart has non-special items, keeping all items");
        return Promise.resolve(cartData);
      }

      // If all items are specials, remove them
      console.log("All items are specials, removing them");

      // Create updates object with all items set to quantity 0
      const updates = {};
      cartData.items.forEach(item => {
        updates[item.key] = 0;
      });

      // Send request to update cart (remove all items)
      return fetch(window.Shopify.routes.root + "cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ updates }),
      })
        .then(response => response.json())
        .then(updatedCart => {
          console.log("All special items removed from cart");
          return updatedCart;
        })
        .catch(error => {
          console.error("Error removing special items:", error);
          return cartData; // Return original data if there's an error
        });
    }

    updateUpsellProducts() {
      var holder = document.querySelector(".slide-out-cart__footer-upsells");
      if (!holder) return;

      // Get the current cart items to check if upsells are already in cart
      fetch(window.Shopify.routes.root + "cart.js")
        .then((response) => response.json())
        .then((cartData) => {
          // Extract variant IDs from cart items
          const cartVariantIds = cartData.items.map(item => item.variant_id.toString());

          // Get related products from the slider
          var relatedProducts = document.querySelectorAll("#shuffle li.splide__slide:not(.splide__slide--clone)");
          var htmlString = "";

          // If we're on a product template page, use related products
          if (document.body.classList.contains("template-product") && relatedProducts.length > 0) {
              Array.from(relatedProducts).slice(0, 2).forEach((product) => {
              try {
                var url = product.querySelector("a").href,
                  imageSrc = product.querySelector("a img").src,
                  name = product.querySelector("h3 a").innerText,
                  upsellID = product.dataset.id,
                  price = 19.99, //Pawfy.returnUpsellPrice(upsellID);
                  isInCart = cartVariantIds.includes(upsellID);

                htmlString += `<div class="slide-out-cart__item" data-cart-item="" data-line-item-key="${upsellID}">
                        <div class="slide-out-cart__item-image">
                            <a href="${url}">
                                <img src="${imageSrc}" alt="${name}">
                            </a>
                        </div>
                        <div class="slide-out-cart__item-details">
                            <h3 class="slide-out-cart__item-title">
                                <a href="${url}">${name}</a>
                            </h3>
                            <div class="slide-out-cart__item-price">
                            £${price}
                            </div>
                            <a href="#" class="button primary upsellAdder" data-added="${isInCart ? 'true' : 'false'}">${isInCart ? 'Added' : 'ADD'}</a>
                        </div>
                    </div>`;
              } catch (e) {
                console.error("Error processing related product:", e);
              }
            });
          } else {
            // Default upsell products if not on a product page or no related products
            const painReliefId = "40864104972323";
            const probioticId = "40864104579107";
            const isPainReliefInCart = cartVariantIds.includes(painReliefId);
            const isProbioticInCart = cartVariantIds.includes(probioticId);

            htmlString = `<div class="slide-out-cart__item" data-cart-item="" data-line-item-key="${painReliefId}">
                        <div class="slide-out-cart__item-image">
                            <a href="#">
                                <img src="https://cdn.shopify.com/s/files/1/0506/0424/5166/files/coverLarge-pain.png?v=1734440226" alt="">
                            </a>
                        </div>
                        <div class="slide-out-cart__item-details">
                            <h3 class="slide-out-cart__item-title">
                                Pain Relief
                            </h3>
                            <div class="slide-out-cart__item-price">
                            £19.99
                            </div>
                            <a href="#" class="button primary upsellAdder" data-added="${isPainReliefInCart ? 'true' : 'false'}">${isPainReliefInCart ? 'Added' : 'ADD'}</a>
                        </div>
                    </div><div class="slide-out-cart__item" data-cart-item="" data-line-item-key="${probioticId}">
                        <div class="slide-out-cart__item-image">
                            <a href="#">
                                <img src="https://cdn.shopify.com/s/files/1/0506/0424/5166/files/coverLarge-probiotic.png?v=1734440226" alt="">
                            </a>
                        </div>
                        <div class="slide-out-cart__item-details">
                            <h3 class="slide-out-cart__item-title">
                              Probiotic
                            </h3>
                            <div class="slide-out-cart__item-price">
                            £19.99
                            </div>
                            <a href="#" class="button primary upsellAdder" data-added="${isProbioticInCart ? 'true' : 'false'}">${isProbioticInCart ? 'Added' : 'ADD'}</a>
                        </div>
                    </div>`;
          }

          // Update the holder with the new HTML
          holder.innerHTML = htmlString;

          // Add event listeners to the upsell adder buttons
          this.initUpsellButtons();
        })
        .catch((error) => {
          console.error("Error fetching cart data for upsells:", error);
        });
    }

    initUpsellButtons() {

      document.querySelectorAll(".upsellAdder").forEach((adder) => {
        adder.addEventListener("click", function () {
          var variantID = adder.closest(".slide-out-cart__item").dataset.lineItemKey;
          var buttonSpan = this;
          var isAdded = adder.dataset.added === "true";

          // Get the price from the item
          var priceElement = adder.closest(".slide-out-cart__item-details").querySelector(".slide-out-cart__item-price");
          var priceText = priceElement.textContent.trim();

          // Extract the numeric price (remove currency symbol and any whitespace)
          var price = parseFloat(priceText.replace(/[^0-9.]/g, ''));

          // Update the subtotal immediately
          var subtotalElement = document.querySelector(".slide-out-cart__subtotal span:last-child");

          if (isAdded) {
            // Item is already added, so remove it
            adder.dataset.added = "false";
            buttonSpan.innerText = "ADD";

            // Subtract price from subtotal
            if (subtotalElement) {
              var currentSubtotal = parseFloat(subtotalElement.textContent.replace(/[^0-9.]/g, ''));
              var newSubtotal = currentSubtotal - price;

              // Format the new subtotal with the same currency format
              const formatter = new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "GBP",
              });

              subtotalElement.textContent = formatter.format(newSubtotal);
            }

            // Send the AJAX request to remove the item from the cart
            var xhr = new XMLHttpRequest();
            xhr.onreadystatechange = function () {
              if (xhr.readyState == 4 && xhr.status == 200) {
                // Dispatch event to update the cart
                document.dispatchEvent(new CustomEvent("cart:updated"));
              }
            };
            xhr.open("POST", "/cart/change.js", true);
            xhr.setRequestHeader("Content-Type", "application/json");

            xhr.send(
              JSON.stringify({
                id: variantID,
                quantity: 0
              })
            );
          } else {
            // Item is not added, so add it
            adder.dataset.added = "true";
            buttonSpan.innerText = "Added";

            // Add price to subtotal
            if (subtotalElement) {
              var currentSubtotal = parseFloat(subtotalElement.textContent.replace(/[^0-9.]/g, ''));
              var newSubtotal = currentSubtotal + price;

              // Format the new subtotal with the same currency format
              const formatter = new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "GBP",
              });

              subtotalElement.textContent = formatter.format(newSubtotal);
            }

            // Send the AJAX request to add the item to the cart
            var xhr = new XMLHttpRequest();
            xhr.onreadystatechange = function () {
              if (xhr.readyState == 4 && xhr.status == 200) {
                // Dispatch event to update the cart
                document.dispatchEvent(new CustomEvent("cart:updated"));
              }
            };
            xhr.open("POST", "/cart/add.js", true);
            xhr.setRequestHeader("Content-Type", "application/json");

            xhr.send(
              JSON.stringify({
                attributes: {
                  Parent: variantID,
                },
                items: [
                  {
                    quantity: 1,
                    id: variantID,
                    properties: {},
                  },
                ],
              })
            );
          }
        });
      });
    }

    updateCartItems(cartData) {
      // Clear loading state
      this.cart?.classList.remove("is-loading");

      // Get the cart body element
      const cartBody = this.cart?.querySelector(".slide-out-cart__body");
      if (!cartBody) return;

      // Check if cart has items
      if (cartData.items && cartData.items.length > 0) {
        console.log("Cart has items:", cartData.items.length);

        // Create cart form with items if it doesn't exist

          cartBody.innerHTML = `
            <form action="${window.Shopify.routes.root}cart" method="post" id="cart-form">
              <div class="slide-out-cart__items"></div>
              <div class="slide-out-cart__footer"></div>
            </form>
          `;
          this.cartForm = document.getElementById("cart-form");
          this.cartItems = this.cart?.querySelector(".slide-out-cart__items");


        // Create HTML for cart items
        let itemsHTML = "";

        cartData.items.forEach((item) => {
          const itemPrice = this.formatMoney(item.final_price);
          const originalPrice = item.original_price > item.final_price ? `<s>${this.formatMoney(item.original_price)}</s>` : "";

          const variantTitle = item.variant_title ? `<p class="slide-out-cart__item-variant">${item.variant_title}</p>` : "";

          itemsHTML += `
            <div class="slide-out-cart__item" data-cart-item data-line-item-key="${item.key}">
              <div class="slide-out-cart__item-image">
                <a href="${item.url}">
                  <img src="${item.image}" alt="${item.title}">
                </a>
              </div>
              <div class="slide-out-cart__item-details">
                <h3 class="slide-out-cart__item-title">
                  <a href="${item.url}">${item.product_title}</a>
                </h3>
                ${variantTitle}
                <div class="slide-out-cart__item-price">
                  ${itemPrice}
                  ${originalPrice}
                </div>

                <div class="slide-out-cart__item-quantity" ${item.product_title.toLowerCase().indexOf("special") > -1 ? "style='display:none'" : ""}>
                  <button type="button" class="quantity-button quantity-down" data-quantity-minus>-</button>
                  <input type="number" name="updates[]" id="updates_${item.key}" value="${item.quantity}" min="0" pattern="[0-9]*" data-quantity-input>
                  <button type="button" class="quantity-button quantity-up" data-quantity-plus>+</button>
                </div>
                <button type="button" class="slide-out-cart__item-remove" data-remove-item>Remove</button>
              </div>
            </div>
          `;
        });

        // Update cart items
        if (this.cartItems) {
          this.cartItems.innerHTML = itemsHTML;
        }

        // Update cart footer
        const cartFooter = this.cart?.querySelector(".slide-out-cart__footer");
        if (cartFooter) {
          const subtotalPrice = this.formatMoney(cartData.total_price);

          let discountsHTML = "";
          if (cartData.cart_level_discount_applications && cartData.cart_level_discount_applications.length > 0) {
            discountsHTML = '<div class="slide-out-cart__discounts">';
            cartData.cart_level_discount_applications.forEach((discount) => {
              discountsHTML += `
                <div class="slide-out-cart__discount">
                  <span>${discount.title}</span>
                  <span>-${this.formatMoney(discount.total_allocated_amount)}</span>
                </div>
              `;
            });
            discountsHTML += "</div>";
          }

          cartFooter.innerHTML = `
          <h2 class="footer-heading">Up To Half Price, Just For You:</h2>
          <div class="slide-out-cart__footer-upsells"></div>
            <div class="slide-out-cart__subtotal">
              <span>Total</span>
              <span>${subtotalPrice}</span>
            </div>
            ${discountsHTML}
            <div class="slide-out-cart__buttons">
              <button type="submit" name="update" class="slide-out-cart__update" style="display:none">Update Cart</button>
              <button type="submit" name="checkout" class="button primary">Checkout</button>
            </div>
          `;
        }

        // Reinitialize event listeners for the new elements
        this.initQuantityButtons();
        this.initRemoveButtons();
        this.initCartForm();
      } else {
        // Show empty cart message
        console.log("Cart is empty");
        cartBody.innerHTML = `
          <form action="${window.Shopify.routes.root}cart" method="post" id="cart-form">
            <div class="slide-out-cart__empty">
              <p>Your cart is currently empty.</p>
              <a href="/pages/shop" class="button primary">SHOP BEST SELLERS</a>
              <br><br>
            
              <button type="submit" name="update" class="slide-out-cart__update" style="display:none">Update Cart</button>
            </div>
          </form>
        `;

        // Update references
        this.cartForm = document.getElementById("cart-form");
        this.cartItems = null;
      }
    }

    formatMoney(cents) {
      // Simple money formatter - can be replaced with Shopify's Currency.formatMoney if available
      const formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "GBP",
      });

      return formatter.format(cents / 100);
    }

    updateCartCount(count) {
      // Update cart count in the header
      const cartCountElements = document.querySelectorAll("[data-cart-count]");
      cartCountElements.forEach((element) => {
        element.textContent = count;

        // Toggle empty class
        if (count === 0) {
          element.classList.add("is-empty");
        } else {
          element.classList.remove("is-empty");
        }
      });
    }

    updateCart() {
      // Fetch the latest cart data
      fetch(window.Shopify.routes.root + "cart.js")
        .then((response) => response.json())
        .then((data) => {
          this.updateCartCount(data.item_count);
        })
        .catch((error) => {
          console.error("Error fetching cart:", error);
        });
    }
  }

  // Initialize the slide-out cart when the DOM is fully loaded
  document.addEventListener("DOMContentLoaded", () => {
    new SlideOutCart();
  });

  // Add to cart AJAX handler
  function addToCartAjax(form) {
    const submitButton = form.querySelector('[type="submit"]');
    const originalButtonText = submitButton.innerHTML;

    submitButton.disabled = true;
    submitButton.innerHTML = "Adding...";

    fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      body: new FormData(form),
    })
      .then((response) => response.json())
      .then((data) => {
        // If there was an error, show it
        if (data.status && data.status !== 200) {
          throw new Error(data.description || "Error adding item to cart");
        }

        // Dispatch event to update the cart
        document.dispatchEvent(new CustomEvent("cart:updated"));

        // Open the slide-out cart
        const cart = document.getElementById("slide-out-cart");
        if (cart && !cart.classList.contains("is-active")) {
          const cartInstance = new SlideOutCart();
          cartInstance.openCart();
        }
      })
      .catch((error) => {
        console.error("Error adding to cart:", error);
        alert(error.message || "There was an error adding this item to your cart. Please try again.");
      })
      .finally(() => {
        submitButton.disabled = false;
        submitButton.innerHTML = originalButtonText;
      });

    return false;
  }

  // Add event listeners to all add to cart forms
  document.addEventListener("DOMContentLoaded", () => {
    const addToCartForms = document.querySelectorAll('form[action="/cart/add"]');
    addToCartForms.forEach((form) => {
      form.addEventListener("submit", function (event) {
        event.preventDefault();
        addToCartAjax(this);
      });
    });
  });
